import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

export const DocumentTemplateGeneralDataComponent = ({
  documentTemplate,
  outputTypes,
  contentTypes,
  targetAreas,
  documentTypes,
  exportTypes,
  onPropertyChanged,
  onLocalDiskFileNameChanged,
  visibleUniqueByDocument,
  validation
}) => {
  const [t] = useTranslation();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={documentTemplate.templateName || ''}
          onChange={event => onPropertyChanged('templateName', event)}
          label={t('DocumentTemplate.TemplateName')}
          error={!isValid(validation.templateName)}
          helperText={getErrors(validation.templateName)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={documentTemplate.templateCode || ''}
          onChange={event => onPropertyChanged('templateCode', event)}
          label={t('DocumentTemplate.TemplateCode')}
          error={!isValid(validation.templateCode)}
          helperText={getErrors(validation.templateCode)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={documentTemplate.outputType}
          options={outputTypes}
          label={t('DocumentTemplate.OutputTypeName')}
          onChange={event => onPropertyChanged('outputType', event)}
          simpleValue
          error={!isValid(validation.outputType)}
          helperText={getErrors(validation.outputType)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          isNumeric
          fullWidth
          decimalScale={0}
          value={documentTemplate.displayOrder}
          onChange={event => {
            if (event !== documentTemplate.displayOrder) onPropertyChanged('displayOrder', event);
          }}
          label={t('DocumentTemplate.DisplayOrder')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={documentTemplate.uspName || ''}
          onChange={event => onPropertyChanged('uspName', event)}
          label={t('DocumentTemplate.USPName')}
        />
      </Grid>
      {documentTemplate.bulkPrintingYN === false && (
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            value={documentTemplate.targetAreaId}
            options={targetAreas}
            label={t('DocumentTemplate.TargetArea')}
            onChange={event => onPropertyChanged('targetAreaId', event)}
            simpleValue
          />
        </Grid>
      )}
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          value={documentTemplate.sharepointContentType}
          options={contentTypes}
          simpleValue
          label={t('DocumentTemplate.ContentType')}
          onChange={event => onPropertyChanged('sharepointContentType', event)}
          isSearchable={true}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          label={t('DocumentTemplate.DocumentType')}
          value={documentTemplate.documentTypeId}
          options={documentTypes}
          onChange={event => onPropertyChanged('documentTypeId', event)}
          simpleValue
          error={!isValid(validation.documentTypeId)}
          helperText={getErrors(validation.documentTypeId)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={documentTemplate.localDiskFileName || ''}
          onChange={event => onPropertyChanged('localDiskFileName', event)}
          onBlur={onLocalDiskFileNameChanged}
          label={t('DocumentTemplate.LocalDiskFileName')}
          error={!isValid(validation.localDiskFileName)}
          helperText={getErrors(validation.localDiskFileName)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          label={t('DocumentTemplate.ExportType')}
          value={{
            id: documentTemplate.exportType
          }}
          labelKey='id'
          options={exportTypes}
          onChange={event => {
            onPropertyChanged('exportType', event ? event.id : null);
          }}
          error={!isValid(validation.exportType)}
          helperText={getErrors(validation.exportType)}
        />
      </Grid>
      {visibleUniqueByDocument && documentTemplate?.bulkPrintingYN === false && (
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={documentTemplate.uniqueByDocument || false}
                onChange={event => onPropertyChanged('uniqueByDocument', event.target.checked)}
                color='primary'
              />
            }
            label={t('DocumentTemplate.UniqueByDocument')}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={documentTemplate.bulkPrintingYN || false}
              onChange={event => onPropertyChanged('bulkPrintingYN', event.target.checked)}
            />
          }
          label={t('DocumentTemplate.BulkPrinting')}
          labelPlacement='end'
        />
      </Grid>
    </Grid>
  );
};

DocumentTemplateGeneralDataComponent.propTypes = {
  documentTemplate: PropTypes.object.isRequired,
  outputTypes: PropTypes.array.isRequired,
  contentTypes: PropTypes.array.isRequired,
  targetAreas: PropTypes.array.isRequired,
  documentTypes: PropTypes.array.isRequired,
  exportTypes: PropTypes.array.isRequired,
  onPropertyChanged: PropTypes.func.isRequired,
  onLocalDiskFileNameChanged: PropTypes.func.isRequired,
  visibleUniqueByDocument: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired
};

export default DocumentTemplateGeneralDataComponent;
